// components/shared/access.js

import { getUserProfile } from "../../services/sharedService";

export const ACTIVE_ORGANIZATION_ROLE_REFRESH_EVENT =
  "active-organization-role-refresh";

export const ROLES = {
  SUPER_ADMIN: "SUPER_ADMIN",
  BRAND_MANAGER: "BRAND_MANAGER",
  ADMIN: "ADMIN",
  MANAGER: "MANAGER",
  GUEST: "GUEST",
};

export const ROLE_ORDER = [
  ROLES.SUPER_ADMIN,
  ROLES.BRAND_MANAGER,
  ROLES.ADMIN,
  ROLES.MANAGER,
  ROLES.GUEST,
];

export const ROLE_INFO = {
  [ROLES.SUPER_ADMIN]: {
    key: ROLES.SUPER_ADMIN,
    label: "Super Admin",
    description:
      "Sole proprietor of the organization. Holds highest authority across all brands.",
    rank: 5,
    isOrgMember: true,
  },

  [ROLES.BRAND_MANAGER]: {
    key: ROLES.BRAND_MANAGER,
    label: "Brand Manager",
    description:
      "User who creates the brand. Holds highest authority within that brand.",
    rank: 4,
    isOrgMember: true,
  },

  [ROLES.ADMIN]: {
    key: ROLES.ADMIN,
    label: "Admin",
    description: "User assigned administrative rights by Brand Manager.",
    rank: 3,
    isOrgMember: true,
  },

  [ROLES.MANAGER]: {
    key: ROLES.MANAGER,
    label: "Manager",
    description: "User assigned limited management and operational rights.",
    rank: 2,
    isOrgMember: true,
  },

  [ROLES.GUEST]: {
    key: ROLES.GUEST,
    label: "Guest",
    description:
      "User with console access but not considered an organizational member and does not appear under People.",
    rank: 1,
    isOrgMember: false,
  },
};

const DEFAULT_ROLE_RULES = {
  canManageAdmins: false,
  canEditOrgInfo: false,
  canEditProfessionalDevelopmentModel: false,
  canManageDepartments: false,
  canManageRoles: false,
  canInviteUsers: false,
  canDeleteUsers: false,
  canAccessTIA: false,
  canEditLeadershipPotential: false,
  canEditInternalMobility: false,
  canViewPeople: false,

  canManageNotifications: false,
  canEditUsers: false,

  canInvitePeople: false,
  canEditPeople: true,

  canDeleteBrand: false,
  canEditBrand: false,
  canCreateBrand: false,

  canAccessBillingSubscription: false,

  canViewInvoices: false,

  canViewPaymentMethod: false,
  canAddPaymentMethod: false,
  canModifyPaymentMethod: false,
  canDeletePaymentMethod: false,

  canViewSubscription: false,
  canAddSubscription: false,
  canModifySubscription: false,
  canDeleteSubscription: false,

  canEnablePartnerCollaboration: false,
  canAddPartner: false,
  canDeletePartner: false,
  canEditPartner: false,
  canBulkRemovePartners: false,
};

export const ROLE_RULES = {
  [ROLES.SUPER_ADMIN]: {
    canManageAdmins: true,
    canEditOrgInfo: true,
    canEditProfessionalDevelopmentModel: true,
    canManageDepartments: true,
    canManageRoles: true,
    canInviteUsers: true,
    canDeleteUsers: true,
    canAccessTIA: true,
    canEditLeadershipPotential: true,
    canEditInternalMobility: true,
    canViewPeople: true,
    canManageNotifications: true,
    canEditUsers: true,

    canInvitePeople: true,
    canEditPeople: true,

    canDeleteBrand: true,
    canEditBrand: true,
    canCreateBrand: true,

    canAccessBillingSubscription: true,

    canViewInvoices: true,

    canViewPaymentMethod: true,
    canAddPaymentMethod: true,
    canModifyPaymentMethod: true,
    canDeletePaymentMethod: true,

    canViewSubscription: true,
    canAddSubscription: true,
    canModifySubscription: true,
    canDeleteSubscription: true,

    canEnablePartnerCollaboration: true,
    canAddPartner: true,
    canDeletePartner: true,
    canEditPartner: true,
    canBulkRemovePartners: true,
  },

  [ROLES.BRAND_MANAGER]: {
    canManageAdmins: true,
    canEditOrgInfo: true,
    canManageDepartments: true,
    canEditProfessionalDevelopmentModel: true,
    canManageRoles: true,
    canInviteUsers: true,
    canDeleteUsers: true,
    canAccessTIA: true,
    canEditLeadershipPotential: true,
    canEditInternalMobility: true,
    canViewPeople: true,
    canManageNotifications: true,
    canEditUsers: true,

    canInvitePeople: true,
    canEditPeople: true,

    canDeleteBrand: true,
    canEditBrand: true,
    canCreateBrand: true,

    canAccessBillingSubscription: false,

    canViewInvoices: false,

    canViewPaymentMethod: false,
    canAddPaymentMethod: false,
    canModifyPaymentMethod: false,
    canDeletePaymentMethod: false,

    canViewSubscription: false,
    canAddSubscription: false,
    canModifySubscription: false,
    canDeleteSubscription: false,
    canEnablePartnerCollaboration: true,
    canAddPartner: true,
    canDeletePartner: true,
    canEditPartner: true,
    canBulkRemovePartners: true,
  },

  [ROLES.ADMIN]: {
    canManageAdmins: false,
    canEditOrgInfo: true,
    canEditProfessionalDevelopmentModel: true,
    canManageDepartments: true,
    canManageRoles: true,
    canInviteUsers: true,
    canDeleteUsers: true,
    canAccessTIA: true,
    canEditLeadershipPotential: true,
    canEditInternalMobility: true,
    canViewPeople: true,
    canManageNotifications: true,
    canEditUsers: true,

    canInvitePeople: true,
    canEditPeople: true,

    canDeleteBrand: true,
    canEditBrand: true,
    canCreateBrand: true,

    canAccessBillingSubscription: false,

    canViewInvoices: false,

    canViewPaymentMethod: false,
    canAddPaymentMethod: false,
    canModifyPaymentMethod: false,
    canDeletePaymentMethod: false,

    canViewSubscription: false,
    canAddSubscription: false,
    canModifySubscription: false,
    canDeleteSubscription: false,

    canEnablePartnerCollaboration: false,
    canAddPartner: false,
    canDeletePartner: false,
    canEditPartner: false,
    canBulkRemovePartners: false,
  },

  [ROLES.MANAGER]: {
    canManageAdmins: false,
    canEditOrgInfo: false,
    canManageDepartments: true,
    canManageRoles: true,
    canEditProfessionalDevelopmentModel: false,
    canInviteUsers: false,
    canDeleteUsers: false,
    canAccessTIA: true,
    canEditLeadershipPotential: true,
    canEditInternalMobility: true,
    canViewPeople: true,
    canManageNotifications: false,
    canEditUsers: false,

    canInvitePeople: true,
    canEditPeople: true,

    canDeleteBrand: false,
    canEditBrand: false,
    canCreateBrand: true,

    canAccessBillingSubscription: false,

    canViewInvoices: false,

    canViewPaymentMethod: false,
    canAddPaymentMethod: false,
    canModifyPaymentMethod: false,
    canDeletePaymentMethod: false,

    canViewSubscription: false,
    canAddSubscription: false,
    canModifySubscription: false,
    canDeleteSubscription: false,
    canEnablePartnerCollaboration: false,
    canAddPartner: false,
    canDeletePartner: false,
    canEditPartner: false,
    canBulkRemovePartners: false,
  },

  [ROLES.GUEST]: {
    canManageAdmins: false,
    canEditOrgInfo: false,
    canEditProfessionalDevelopmentModel: false,

    canManageDepartments: false,
    canManageRoles: false,
    canInviteUsers: false,
    canDeleteUsers: false,
    canAccessTIA: true,
    canEditLeadershipPotential: true,
    canEditInternalMobility: true,
    canViewPeople: true,
    canManageNotifications: false,
    canEditUsers: false,

    canInvitePeople: false,
    canEditPeople: true,

    canDeleteBrand: false,
    canEditBrand: false,
    canCreateBrand: true,

    canAccessBillingSubscription: false,

    canViewInvoices: false,

    canViewPaymentMethod: false,
    canAddPaymentMethod: false,
    canModifyPaymentMethod: false,
    canDeletePaymentMethod: false,

    canViewSubscription: false,
    canAddSubscription: false,
    canModifySubscription: false,
    canDeleteSubscription: false,
    canEnablePartnerCollaboration: false,
    canAddPartner: false,
    canDeletePartner: false,
    canEditPartner: false,
    canBulkRemovePartners: false,
  },
};

let cachedLoggedInUserRole = null;
let cachedTenantId = null;
let roleFetchPromise = null;

export function normalizeRole(rawRole) {
  if (!rawRole) return null;

  const value = String(rawRole).trim().toLowerCase();

  const roleMap = {
    "super admin": ROLES.SUPER_ADMIN,
    super_admin: ROLES.SUPER_ADMIN,
    superadmin: ROLES.SUPER_ADMIN,

    "brand manager": ROLES.BRAND_MANAGER,
    brand_manager: ROLES.BRAND_MANAGER,
    brandmanager: ROLES.BRAND_MANAGER,

    admin: ROLES.ADMIN,
    manager: ROLES.MANAGER,
    guest: ROLES.GUEST,
  };

  return roleMap[value] || null;
}

export function getSingleRoleValue(role) {
  if (Array.isArray(role)) {
    return role[0] || "";
  }

  return role || "";
}

export function getHighestRole(rawRoles) {
  const roles = Array.isArray(rawRoles) ? rawRoles : [rawRoles];

  return (
    roles
      .map(normalizeRole)
      .filter(Boolean)
      .sort((roleA, roleB) => {
        const rankA = ROLE_INFO[roleA]?.rank || 0;
        const rankB = ROLE_INFO[roleB]?.rank || 0;
        return rankB - rankA;
      })[0] || null
  );
}

const extractRolesFromProfileResponse = (profileResponse) => {
  return profileResponse?.data?.role || profileResponse?.role || [];
};

export function setLoggedInUserRole(rawRoles, tenantId) {
  cachedLoggedInUserRole = getHighestRole(rawRoles);
  cachedTenantId = tenantId || cachedTenantId;
  return cachedLoggedInUserRole;
}

export function clearLoggedInUserRole() {
  cachedLoggedInUserRole = null;
  cachedTenantId = null;
  roleFetchPromise = null;
}

export async function loadLoggedInUserRole(
  mainTenantId,
  { force = false } = {},
) {
  if (!mainTenantId) {
    clearLoggedInUserRole();
    return null;
  }

  const isSameTenant = String(cachedTenantId || "") === String(mainTenantId);

  if (cachedLoggedInUserRole && isSameTenant && !force) {
    return cachedLoggedInUserRole;
  }

  if (roleFetchPromise && !force) {
    return roleFetchPromise;
  }

  roleFetchPromise = getUserProfile(mainTenantId)
    .then((profileResponse) => {
      const roles = extractRolesFromProfileResponse(profileResponse);
      return setLoggedInUserRole(roles, mainTenantId);
    })
    .catch((error) => {
      console.error("Failed to load logged-in user role:", error);
      clearLoggedInUserRole();
      return null;
    })
    .finally(() => {
      roleFetchPromise = null;
    });

  return roleFetchPromise;
}

export function getLoggedInUserRole() {
  return cachedLoggedInUserRole;
}

export function getRoleInfo(role = getLoggedInUserRole()) {
  const normalizedRole = normalizeRole(role);
  return ROLE_INFO[normalizedRole] || null;
}

export function getRoleLegend() {
  return ROLE_ORDER.map((role) => ({
    ...ROLE_INFO[role],
  }));
}

export function getRoleRules(role = getLoggedInUserRole()) {
  const normalizedRole = normalizeRole(role);
  return ROLE_RULES[normalizedRole] || DEFAULT_ROLE_RULES;
}

export function isOrgMember(role = getLoggedInUserRole()) {
  const normalizedRole = normalizeRole(role);
  return ROLE_INFO[normalizedRole]?.isOrgMember === true;
}

export function isGuest(role = getLoggedInUserRole()) {
  return normalizeRole(role) === ROLES.GUEST;
}

export function shouldShowInPeopleList(targetRole) {
  return normalizeRole(targetRole) !== ROLES.GUEST;
}

export function canAssignDepartment(targetRole) {
  return normalizeRole(targetRole) !== ROLES.GUEST;
}

export function canAssignOrganizationalRole(targetRole) {
  return normalizeRole(targetRole) !== ROLES.GUEST;
}

export function canManageTarget(actorRole, targetRole) {
  const normalizedActorRole = normalizeRole(actorRole);
  const normalizedTargetRole = normalizeRole(getSingleRoleValue(targetRole));

  if (!normalizedActorRole || !normalizedTargetRole) return false;
  if (normalizedTargetRole === ROLES.SUPER_ADMIN) return false;

  const actorRank = ROLE_INFO[normalizedActorRole]?.rank || 0;
  const targetRank = ROLE_INFO[normalizedTargetRole]?.rank || 0;

  return actorRank > targetRank;
}

export function canEditRoleDropdown(targetRole) {
  const actorRole = getLoggedInUserRole();
  const normalizedTargetRole = normalizeRole(getSingleRoleValue(targetRole));

  if (!actorRole || !normalizedTargetRole) return false;

  return canManageTarget(actorRole, normalizedTargetRole);
}

export function canAddOrDeleteAdmin(targetRole = ROLES.ADMIN) {
  const actorRole = getLoggedInUserRole();
  const rules = getRoleRules(actorRole);
  const normalizedTargetRole = normalizeRole(targetRole);

  if (!rules.canManageAdmins) return false;
  if (normalizedTargetRole !== ROLES.ADMIN) return false;

  return canManageTarget(actorRole, normalizedTargetRole);
}

export function canEditUser(targetRole) {
  const actorRole = getLoggedInUserRole();
  const rules = getRoleRules(actorRole);

  if (!rules.canEditUsers) return false;

  return canManageTarget(actorRole, targetRole);
}

export function canDeleteUser(targetRole) {
  const actorRole = getLoggedInUserRole();
  const rules = getRoleRules(actorRole);

  if (!rules.canDeleteUsers) return false;

  return canManageTarget(actorRole, targetRole);
}

/**
 * Organization Info Page
 */
export function getOrganizationInfoAccess() {
  const rules = getRoleRules();

  return {
    canViewPage: true,
    canEditOrgInfo: rules.canEditOrgInfo,
    canManageDepartments: rules.canManageDepartments,
    canAddDepartment: rules.canManageDepartments,
    canDeleteDepartment: rules.canManageDepartments,
    canFetchDescription: rules.canEditOrgInfo,
    canSaveOrgInfo: rules.canEditOrgInfo,
    canEditProfessionalDevelopmentModel:
      rules.canEditProfessionalDevelopmentModel,
  };
}

/**
 * User Management Page
 */
export function getUserManagementAccess() {
  const rules = getRoleRules();

  return {
    canViewPage: true,
    canInviteUsers: rules.canInviteUsers,
    canEditUsers: rules.canEditUsers,
    canDeleteUsers: rules.canDeleteUsers,
    canManageAdmins: rules.canManageAdmins,
    canAddAdmins: rules.canManageAdmins,
    canDeleteAdmins: rules.canManageAdmins,
    canOpenUserActions:
      rules.canInviteUsers ||
      rules.canEditUsers ||
      rules.canDeleteUsers ||
      rules.canManageAdmins,
  };
}

/**
 * Job Roles Page
 */
export function getJobRolesAccess() {
  const rules = getRoleRules();

  return {
    canViewPage: true,
    canManageRoles: rules.canManageRoles,
    canAddRole: rules.canManageRoles,
    canEditRole: rules.canManageRoles,
    canDeleteRole: rules.canManageRoles,
    canSaveRole: rules.canManageRoles,
  };
}

/**
 * People Page
 */
export function getPeopleAccess() {
  const rules = getRoleRules();

  return {
    canViewPage: true,
    canViewPeople: rules.canViewPeople,
    canEditUsers: rules.canEditUsers,
    canDeleteUsers: rules.canDeleteUsers,
    hideGuestsFromPeopleList: true,
    canInvitePeople: rules.canInvitePeople,
    canEditPeople: rules.canEditPeople,
  };
}

/**
 * TIA Page
 */
export function getTIAAccess() {
  const rules = getRoleRules();

  return {
    canViewPage: true,
    canAccessTIA: rules.canAccessTIA,
    canUseTIA: rules.canAccessTIA,
  };
}

/**
 * Leadership Potential Page
 */
export function getLeadershipPotentialAccess() {
  const rules = getRoleRules();

  return {
    canViewPage: true,
    canEditLeadershipPotential: rules.canEditLeadershipPotential,
    canCreateLeadershipPotential: rules.canEditLeadershipPotential,
    canSaveLeadershipPotential: rules.canEditLeadershipPotential,
  };
}

/**
 * Internal Mobility Page
 */
export function getInternalMobilityAccess() {
  const rules = getRoleRules();

  return {
    canViewPage: true,
    canEditInternalMobility: rules.canEditInternalMobility,
    canCreateInternalMobility: rules.canEditInternalMobility,
    canSaveInternalMobility: rules.canEditInternalMobility,
  };
}

/**
 * Brands Page
 */
export function getBrandsAccess() {
  const rules = getRoleRules();

  return {
    canCreateBrand: rules.canCreateBrand,
    canEditBrand: rules.canEditBrand,
    canDeleteBrand: rules.canDeleteBrand,
  };
}

/**
 * Billing & Subscription Page
 */
export function getBillingSubscriptionAccess() {
  const rules = getRoleRules();

  return {
    canViewPage: rules.canAccessBillingSubscription,

    invoices: {
      canView: rules.canViewInvoices,
      canAdd: false,
      canModify: false,
      canDelete: false,
    },

    paymentMethod: {
      canView: rules.canViewPaymentMethod,
      canAdd: rules.canAddPaymentMethod,
      canModify: rules.canModifyPaymentMethod,
      canDelete: rules.canDeletePaymentMethod,
    },

    subscription: {
      canView: rules.canViewSubscription,
      canAdd: rules.canAddSubscription,
      canModify: rules.canModifySubscription,
      canDelete: rules.canDeleteSubscription,
    },
  };
}

/**
 * Notifications Page
 */
export function getNotificationsAccess() {
  const rules = getRoleRules();

  return {
    canViewPage: true,
    canManageNotifications: rules.canManageNotifications,
    canEditNotifications: rules.canManageNotifications,
    canSaveNotifications: rules.canManageNotifications,
  };
}

export function canAssignRole(targetRole) {
  const actorRole = getLoggedInUserRole();
  return canManageTarget(actorRole, targetRole);
}

export function getAssignableRoleOptions(options = []) {
  return options.map((option) => ({
    ...option,
    disabled: !canAssignRole(option.value),
  }));
}

/**
 * One optional helper if you want everything together.
 */
export function getConsoleAccess() {
  return {
    role: getLoggedInUserRole(),
    roleInfo: getRoleInfo(),
    organizationInfo: getOrganizationInfoAccess(),
    userManagement: getUserManagementAccess(),
    jobRoles: getJobRolesAccess(),
    people: getPeopleAccess(),
    tia: getTIAAccess(),
    leadershipPotential: getLeadershipPotentialAccess(),
    internalMobility: getInternalMobilityAccess(),
    brands: getBrandsAccess(),
    billingSubscription: getBillingSubscriptionAccess(),
    notifications: getNotificationsAccess(),
  };
}

//  partners management access
export function getPartnersManagementAccess() {
  const rules = getRoleRules();
  return {
    canViewPage: true,
    canEnablePartnerCollaboration: rules.canEnablePartnerCollaboration,
    canAddPartner: rules.canAddPartner,
    canDeletePartner: rules.canDeletePartner,
    canEditPartner: rules.canEditPartner,
    canBulkRemovePartners: rules.canBulkRemovePartners,
  };
}
